import React, { useState } from 'react';
import { LucideIcon } from '../../components/UI/LucideIcon';
import { GradientButton, Spinner, Turnstile } from '../../components/UI/index';
import { RequisitosSenha, senhaValida, MSG_SENHA_INVALIDA } from '../../components/UI/RequisitosSenha';
import { login, registro, confirmarCadastro, solicitarRedefinicao, redefinirSenha, garantirSessao, continuarLogado, esquecerDispositivo } from '../../services/authApi';

function InputField({ label, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:4}}>
      <label style={{fontSize:11,fontWeight:700,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'.08em'}}>{label}</label>
      <div className="lev-input-glow" style={{borderRadius:'var(--radius-pill)',border:`1.5px solid ${focused?'var(--brand-purple)':'var(--gray-200)'}`,boxShadow:focused?'0 0 0 4px rgba(158,54,239,.14)':'none',transition:'all .2s'}}>
        <input {...props} onFocus={e=>{setFocused(true);props.onFocus&&props.onFocus(e);}} onBlur={e=>{setFocused(false);props.onBlur&&props.onBlur(e);}}
          style={{width:'100%',height:44,border:'none',background:'transparent',padding:'0 18px',fontSize:15,fontFamily:'var(--font-sans)',color:'var(--brand-indigo)',outline:'none'}}/>
      </div>
    </div>
  );
}

function ErrorMsg({ children }) {
  if (!children) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#d63333',fontWeight:600}}>
      <LucideIcon name="alert-circle" size={15}/>{children}
    </div>
  );
}

function SuccessMsg({ children }) {
  if (!children) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#5f7d1f',fontWeight:600}}>
      <LucideIcon name="check-circle" size={15}/>{children}
    </div>
  );
}

// metade da marca — estática, igual nos modos login/signup/confirm/forgot/reset
function BrandPanel({ parallax }) {
  return (
    <aside className="lev-login-brand">
      <img src="/bg-color.svg" alt="" className="lev-login-mark lev-watermark-float"
        style={{transform:`translate3d(${parallax.x*-16}px,${parallax.y*-12}px,0)`}}/>
      <img src="/logo-leverage-white.svg" alt="Leverage Securitizadora" className="lev-login-logo-w"/>
      <div className="lev-login-brand-body">
        <h2>Securitização com Gestão Inteligente</h2>
        <p>Unindo Dados, Tecnologia e Pessoas para alavancar o sucesso do seu negócio!</p>
      </div>
      <ul className="lev-login-trust">
        <li><span className="lev-trust-ic"><LucideIcon name="shield-check" size={18}/></span><span className="lev-trust-tx">Enquadramento dos Fundos, Razões e Índices</span></li>
        <li><span className="lev-trust-ic"><LucideIcon name="clipboard-list" size={18}/></span><span className="lev-trust-tx">Obrigações Gerais, Garantias e Destinação de Recursos</span></li>
        <li><span className="lev-trust-ic"><LucideIcon name="banknote" size={18}/></span><span className="lev-trust-tx">PU diário e histórico de pagamentos das emissões.</span></li>
      </ul>
    </aside>
  );
}

function FormLogo() {
  return <img src="/logo-leverage.svg" alt="Leverage" className="lev-login-form-logo" onError={e=>{e.target.style.display='none';}}/>;
}

// "Continuar como Nome (e-mail)?" — aparece no lugar do form quando a sessão
// expirou (ou F5 com ela expirada) num navegador que já tinha login, sem "Lembrar
// de mim" (com ele, App continua sozinho e esta tela nem aparece). Continuar é um
// login de verdade pro backend (sessão nova, conta como acesso); só pula a senha.
// "Sim, e lembrar de mim" continua e passa o navegador pro modo automático.
// "Entrar com outra conta" só mostra o form, sem esquecer o dispositivo (o
// próximo login, de quem for, substitui o lembrado); "Esquecer" esquece de vez.
function ContinuarCard({ dispositivo, onLogin, onOutraConta, onEsquecido }) {
  const [loading, setLoading] = useState(null); // null | 'continuar' | 'lembrar'
  const [err, setErr] = useState('');
  const nomeCompleto = `${dispositivo.nome || ''} ${dispositivo.sobrenome || ''}`.trim();
  const iniciais = ((dispositivo.nome || '')[0] || '').toUpperCase() + ((dispositivo.sobrenome || '')[0] || '').toUpperCase();

  const continuar = async lembrar => {
    if (loading) return;
    setErr(''); setLoading(lembrar ? 'lembrar' : 'continuar');
    try {
      const result = await continuarLogado({ lembrar });
      onLogin({ user: result.user, expiresIn: result.expiresIn, followed: result.preferenciasEmissao });
    } catch (e) {
      // 401: dispositivo expirou/foi revogado no meio do caminho — cai no form normal.
      if (e.status === 401) { onEsquecido('Não foi possível continuar automaticamente. Entre com e-mail e senha.'); return; }
      setErr(e.message || 'Não foi possível continuar.');
      setLoading(null);
    }
  };

  const naoSouEu = () => {
    esquecerDispositivo().catch(() => {});
    onEsquecido('');
  };

  return (
    <div className="lev-login-form">
      <FormLogo/>
      <h1 className="lev-login-h">Bem-vindo de volta</h1>
      <p className="lev-login-hsub">Deseja continuar como {dispositivo.nome || 'esta conta'}?</p>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',border:'1.5px solid var(--gray-200)',borderRadius:16,background:'rgba(255,255,255,.5)'}}>
          <div aria-hidden="true" style={{width:40,height:40,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',background:'var(--brand-purple)',color:'#fff',fontWeight:700,fontSize:14}}>
            {iniciais || <LucideIcon name="user" size={18} color="#fff"/>}
          </div>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:700,fontSize:15,color:'var(--brand-indigo)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{nomeCompleto}</div>
            {dispositivo.email && (
              <div style={{fontSize:13,color:'var(--gray-500)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{dispositivo.email}</div>
            )}
          </div>
        </div>
        <ErrorMsg>{err}</ErrorMsg>
        <GradientButton loading={!!loading} type="button" onClick={() => continuar(false)}>
          {loading === 'continuar' ? <><Spinner/> Entrando…</> : <>Sim, continuar <LucideIcon name="arrow-right" size={18} color="#fff"/></>}
        </GradientButton>
        <button type="button" onClick={() => continuar(true)} disabled={!!loading} className="lev-auth-alt"
          title="Nas próximas vezes o portal continua sozinho, sem perguntar. Não use em computador compartilhado.">
          {loading === 'lembrar' ? <><Spinner color="var(--brand-purple)"/> Entrando…</> : <><LucideIcon name="check" size={17}/> Sim, e lembrar de mim</>}
        </button>
        <button type="button" onClick={onOutraConta} disabled={!!loading} className="lev-auth-alt">
          <LucideIcon name="users" size={17}/> Não, entrar com outra conta
        </button>
        <p style={{textAlign:'center',margin:'2px 0 0',fontSize:12,color:'var(--gray-500)'}}>
          Não é {dispositivo.nome}?{' '}
          <button type="button" onClick={naoSouEu} disabled={!!loading} style={{fontWeight:600,fontSize:12,color:'var(--brand-purple)',background:'none',border:'none',cursor:'pointer',padding:0}}>
            Esquecer esta conta neste dispositivo
          </button>
        </p>
      </div>
    </div>
  );
}

function LoginCard({ onLogin, onGuest, toSignup, toForgot, initialEmail, notice, initialErr }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(initialErr || '');
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestErr, setGuestErr] = useState('');

  const submit = async e => {
    e.preventDefault();
    if (!email.trim() || !senha) { setErr('Informe e-mail e senha.'); return; }
    setErr(''); setLoading(true);
    try {
      const result = await login({ email: email.trim().toLowerCase(), senha, lembrar });
      onLogin({ user: result.user, expiresIn: result.expiresIn, followed: result.preferenciasEmissao });
    } catch (e) {
      setErr(e.message || 'Não foi possível entrar.');
      setLoading(false);
    }
  };

  const accessAsGuest = async () => {
    setGuestErr(''); setGuestLoading(true);
    try {
      onGuest(await garantirSessao());
    } catch (e) {
      setGuestErr(e.message || 'Não foi possível acessar sem conta.');
      setGuestLoading(false);
    }
  };

  return (
    <form className="lev-login-form" onSubmit={submit}>
      <FormLogo/>
      <h1 className="lev-login-h">Acesse o Portal</h1>
      <p className="lev-login-hsub">Bem-vindo de volta. Entre com suas credenciais.</p>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <SuccessMsg>{notice}</SuccessMsg>
        <InputField label="E-mail" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="seu@email.com.br" autoComplete="username"/>
        <div style={{position:'relative'}}>
          <InputField label="Senha" type={showPw?'text':'password'} value={senha} onChange={e=>setSenha(e.target.value)} placeholder="••••••••" autoComplete="current-password"/>
          <button type="button" onClick={()=>setShowPw(s=>!s)} aria-label={showPw?'Ocultar senha':'Mostrar senha'}
            style={{position:'absolute',right:16,bottom:12,background:'none',border:'none',cursor:'pointer',color:'var(--gray-500)'}}>
            <LucideIcon name={showPw?'eye-off':'eye'} size={18}/>
          </button>
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,fontSize:13}}>
          <label style={{display:'inline-flex',alignItems:'center',gap:7,cursor:'pointer',color:'var(--gray-500)',fontWeight:600}}
            title="Quando a sessão expirar, o portal continua sozinho, sem pedir senha nem confirmação. Não use em computador compartilhado.">
            <input type="checkbox" checked={lembrar} onChange={e=>setLembrar(e.target.checked)}
              style={{width:15,height:15,margin:0,accentColor:'var(--brand-purple)',cursor:'pointer'}}/>
            Lembrar de mim
          </label>
          <button type="button" onClick={()=>toForgot(email)} style={{fontWeight:600,color:'var(--brand-purple)',background:'none',border:'none',cursor:'pointer',padding:0}}>Esqueci a senha</button>
        </div>
        <ErrorMsg>{err}</ErrorMsg>
        <GradientButton loading={loading}>
          {loading ? <><Spinner/> Entrando…</> : <>Acessar portal <LucideIcon name="arrow-right" size={18} color="#fff"/></>}
        </GradientButton>
        <div className="lev-auth-sep"><span>ou</span></div>
        <button type="button" onClick={toSignup} className="lev-auth-alt">
          <LucideIcon name="user-plus" size={17}/> Criar uma conta
        </button>
        <button type="button" onClick={accessAsGuest} disabled={guestLoading} className="lev-auth-alt">
          {guestLoading ? <><Spinner color="var(--brand-purple)"/> Entrando…</> : <><LucideIcon name="user" size={17}/> Acessar sem conta</>}
        </button>
        <ErrorMsg>{guestErr}</ErrorMsg>
        <p style={{textAlign:'center',margin:'2px 0 0',fontSize:12,color:'var(--gray-500)',lineHeight:1.5}}>
          <LucideIcon name="lock" size={12} style={{display:'inline-flex',verticalAlign:'-2px',marginRight:5}}/>
          Sem conta, você escolhe as emissões a cada acesso — crie uma conta para salvar sua seleção.
        </p>
      </div>
    </form>
  );
}

function SignupCard({ toLogin, toConfirm }) {
  const [nome, setNome] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [email, setEmail] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const submit = async e => {
    e.preventDefault();
    if (!nome.trim()||!sobrenome.trim()||!email.trim()||!senha) { setErr('Preencha nome, sobrenome, e-mail e senha.'); return; }
    if (!senhaValida(senha)) { setErr(MSG_SENHA_INVALIDA); return; }
    if (!captchaToken) { setErr('Confirme a verificação de segurança.'); return; }
    setErr(''); setLoading(true);
    const payload = { nome: nome.trim(), sobrenome: sobrenome.trim(), email: email.trim().toLowerCase(), empresa: empresa.trim() || null };
    try {
      await registro({ ...payload, captcha_token: captchaToken });
      toConfirm({ ...payload, senha });
    } catch (e) {
      setErr(e.message || 'Não foi possível criar a conta.');
      // token do Turnstile é de uso único -- remonta o widget pra pegar um novo
      setCaptchaToken('');
      setCaptchaKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="lev-login-form" onSubmit={submit}>
      <button type="button" onClick={toLogin} className="lev-auth-back">
        <LucideIcon name="arrow-left" size={15}/> Voltar ao login
      </button>
      <h1 className="lev-login-h">Criar conta</h1>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <InputField label="Nome" value={nome} onChange={e=>setNome(e.target.value)} placeholder="Nome" autoComplete="given-name"/>
          <InputField label="Sobrenome" value={sobrenome} onChange={e=>setSobrenome(e.target.value)} placeholder="Sobrenome" autoComplete="family-name"/>
        </div>
        <InputField label="E-mail" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="ana@empresa.com.br" autoComplete="email"/>
        <InputField label="Empresa (opcional)" value={empresa} onChange={e=>setEmpresa(e.target.value)} placeholder="Leverage" autoComplete="organization"/>
        <InputField label="Senha" type="password" value={senha} onChange={e=>setSenha(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password"/>
        <RequisitosSenha senha={senha}/>
        <Turnstile key={captchaKey} onToken={setCaptchaToken}/>
        <ErrorMsg>{err}</ErrorMsg>
        <GradientButton loading={loading}>
          {loading ? <><Spinner/> Enviando código…</> : <>Criar conta <LucideIcon name="arrow-right" size={18} color="#fff"/></>}
        </GradientButton>
      </div>
    </form>
  );
}

function ConfirmCadastroCard({ pending, onConfirmed, toSignup }) {
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [resent, setResent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const submit = async e => {
    e.preventDefault();
    const codigoLimpo = codigo.trim();
    if (codigoLimpo.length !== 6) { setErr('Informe o código de 6 dígitos.'); return; }
    setErr(''); setBlocked(false); setResent(false); setLoading(true);
    try {
      await confirmarCadastro({ email: pending.email, codigo: codigoLimpo, senha: pending.senha });
      const result = await login({ email: pending.email, senha: pending.senha });
      onConfirmed(result);
    } catch (e) {
      setErr(e.message || 'Código inválido ou expirado.');
      setBlocked(!!e.blocked);
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!captchaToken) { setErr('Confirme a verificação de segurança para reenviar.'); return; }
    setResending(true); setErr(''); setBlocked(false); setResent(false);
    try {
      await registro({ nome: pending.nome, sobrenome: pending.sobrenome, email: pending.email, empresa: pending.empresa, captcha_token: captchaToken });
      setResent(true);
      setCodigo('');
    } catch (e) {
      setErr(e.message || 'Não foi possível enviar um novo código.');
    } finally {
      setResending(false);
      setCaptchaToken('');
      setCaptchaKey(k => k + 1);
    }
  };

  return (
    <form className="lev-login-form" onSubmit={submit}>
      <button type="button" onClick={toSignup} className="lev-auth-back">
        <LucideIcon name="arrow-left" size={15}/> Voltar
      </button>
      <h1 className="lev-login-h">Confirme seu e-mail</h1>
      <p className="lev-login-hsub">
        Enviamos um código de 6 dígitos para <strong>{pending.email}</strong>. Ele vale por 60 minutos.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <InputField label="Código de verificação" value={codigo}
          onChange={e=>setCodigo(e.target.value.replace(/\D/g,'').slice(0,6))}
          placeholder="000000" inputMode="numeric" maxLength={6} autoComplete="one-time-code"/>
        <ErrorMsg>{err}</ErrorMsg>
        {resent && !err && <SuccessMsg>Novo código enviado.</SuccessMsg>}
        <GradientButton loading={loading}>
          {loading ? <><Spinner/> Confirmando…</> : <>Confirmar e entrar <LucideIcon name="arrow-right" size={18} color="#fff"/></>}
        </GradientButton>
        <p style={{fontSize:12,color:'var(--gray-500)',margin:'2px 0 0'}}>Não recebeu? Confirme abaixo e reenviamos:</p>
        <Turnstile key={captchaKey} onToken={setCaptchaToken}/>
        <button type="button" onClick={resend} disabled={resending} className="lev-auth-alt">
          {resending ? <><Spinner color="var(--brand-purple)"/> Enviando…</> : <><LucideIcon name="refresh-cw" size={16}/> {blocked ? 'Solicitar novo código' : 'Não recebi o código'}</>}
        </button>
      </div>
    </form>
  );
}

function ForgotPasswordCard({ initialEmail, onSent, toLogin }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const submit = async e => {
    e.preventDefault();
    if (!email.trim()) { setErr('Informe seu e-mail.'); return; }
    if (!captchaToken) { setErr('Confirme a verificação de segurança.'); return; }
    setErr(''); setLoading(true);
    try {
      await solicitarRedefinicao({ email: email.trim().toLowerCase(), captcha_token: captchaToken });
      onSent(email.trim().toLowerCase());
    } catch (e) {
      setErr(e.message || 'Não foi possível enviar o código.');
      setCaptchaToken('');
      setCaptchaKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="lev-login-form" onSubmit={submit}>
      <button type="button" onClick={toLogin} className="lev-auth-back">
        <LucideIcon name="arrow-left" size={15}/> Voltar ao login
      </button>
      <h1 className="lev-login-h">Esqueci minha senha</h1>
      <p className="lev-login-hsub">
        Informe o e-mail da sua conta e enviaremos um código para redefinir a senha.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <InputField label="E-mail" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="seu@email.com.br" autoComplete="email"/>
        <Turnstile key={captchaKey} onToken={setCaptchaToken}/>
        <ErrorMsg>{err}</ErrorMsg>
        <GradientButton loading={loading}>
          {loading ? <><Spinner/> Enviando…</> : <>Enviar código <LucideIcon name="arrow-right" size={18} color="#fff"/></>}
        </GradientButton>
      </div>
    </form>
  );
}

function ResetPasswordCard({ email, onReset, toForgot, toLogin }) {
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [blocked, setBlocked] = useState(false);

  const submit = async e => {
    e.preventDefault();
    const codigoLimpo = codigo.trim();
    if (codigoLimpo.length !== 6) { setErr('Informe o código de 6 dígitos.'); return; }
    if (!senhaValida(novaSenha)) { setErr(MSG_SENHA_INVALIDA); return; }
    if (novaSenha !== confirmSenha) { setErr('As senhas não coincidem.'); return; }
    setErr(''); setBlocked(false); setLoading(true);
    try {
      await redefinirSenha({ email, codigo: codigoLimpo, nova_senha: novaSenha });
      onReset(email);
    } catch (e) {
      setErr(e.message || 'Código inválido ou expirado.');
      setBlocked(!!e.blocked);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="lev-login-form" onSubmit={submit}>
      <button type="button" onClick={toLogin} className="lev-auth-back">
        <LucideIcon name="arrow-left" size={15}/> Voltar ao login
      </button>
      <h1 className="lev-login-h">Defina sua nova senha</h1>
      <p className="lev-login-hsub">
        Digite o código enviado para <strong>{email}</strong> e escolha uma nova senha.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:16}}>
        <InputField label="Código de verificação" value={codigo}
          onChange={e=>setCodigo(e.target.value.replace(/\D/g,'').slice(0,6))}
          placeholder="000000" inputMode="numeric" maxLength={6} autoComplete="one-time-code"/>
        <InputField label="Nova senha" type="password" value={novaSenha} onChange={e=>setNovaSenha(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password"/>
        <RequisitosSenha senha={novaSenha}/>
        <InputField label="Confirmar nova senha" type="password" value={confirmSenha} onChange={e=>setConfirmSenha(e.target.value)} placeholder="Repita a nova senha" autoComplete="new-password"/>
        <ErrorMsg>{err}</ErrorMsg>
        <GradientButton loading={loading}>
          {loading ? <><Spinner/> Salvando…</> : <>Redefinir senha <LucideIcon name="check" size={18} color="#fff"/></>}
        </GradientButton>
        {blocked && (
          <button type="button" onClick={toForgot} className="lev-auth-alt">
            <LucideIcon name="refresh-cw" size={16}/> Solicitar novo código
          </button>
        )}
      </div>
    </form>
  );
}

export default function Login2({ onLogin, onGuest, initialMode, dispositivoLembrado, onDispositivoEsquecido }) {
  const [mode, setMode] = useState(initialMode || 'login');
  // "Entrar com outra conta" só esconde o card de "Continuar como" nesta visita.
  const [outraConta, setOutraConta] = useState(false);
  // Motivo exibido no form quando o "Continuar como" falhou (dispositivo expirado/revogado).
  const [avisoContinuar, setAvisoContinuar] = useState('');
  const [pending, setPending] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [loginInfo, setLoginInfo] = useState({ email: '', notice: '' });
  const [parallax, setParallax] = useState({x:0,y:0});
  const onMove = e => {
    const cx = window.innerWidth/2, cy = window.innerHeight/2;
    setParallax({x:(e.clientX-cx)/cx, y:(e.clientY-cy)/cy});
  };

  const goLoginWithNotice = (email, notice) => { setLoginInfo({ email: email||'', notice }); setMode('login'); };

  const handleConfirmed = result => {
    onLogin({ user: result.user, expiresIn: result.expiresIn, followed: result.preferenciasEmissao });
  };

  return (
    <div className="lev-login" onMouseMove={onMove}>
      <div className="lev-login-stage">
        <img src="/bg-color.svg" alt="" className="lev-login-x x1 lev-watermark-float"
          style={{transform:`translate3d(${parallax.x*-34}px,${parallax.y*-28}px,0)`}}/>
        <img src="/bg-color.svg" alt="" className="lev-login-x x2"
          style={{transform:`translate3d(${parallax.x*26}px,${parallax.y*22}px,0) rotate(18deg)`}}/>
      </div>
      <div className="lev-login-card">
        <BrandPanel parallax={parallax}/>
        <div className="lev-login-main">
          {mode==='login' && dispositivoLembrado && !outraConta && (
            <ContinuarCard dispositivo={dispositivoLembrado} onLogin={onLogin}
              onOutraConta={()=>setOutraConta(true)}
              onEsquecido={aviso=>{ setAvisoContinuar(aviso); onDispositivoEsquecido(); }}/>
          )}
          {mode==='login' && (!dispositivoLembrado || outraConta) && (
            <LoginCard onLogin={onLogin} onGuest={onGuest}
              toSignup={()=>{setLoginInfo({email:'',notice:''});setMode('signup');}}
              toForgot={email=>{setResetEmail(email||'');setMode('forgot');}}
              initialEmail={loginInfo.email} notice={loginInfo.notice} initialErr={avisoContinuar}/>
          )}
          {mode==='signup' && (
            <SignupCard toLogin={()=>setMode('login')} toConfirm={data=>{setPending(data);setMode('confirm');}}/>
          )}
          {mode==='confirm' && pending && (
            <ConfirmCadastroCard pending={pending} onConfirmed={handleConfirmed} toSignup={()=>setMode('signup')}/>
          )}
          {mode==='forgot' && (
            <ForgotPasswordCard initialEmail={resetEmail} toLogin={()=>setMode('login')}
              onSent={email=>{setResetEmail(email);setMode('reset');}}/>
          )}
          {mode==='reset' && (
            <ResetPasswordCard email={resetEmail} toForgot={()=>setMode('forgot')} toLogin={()=>setMode('login')}
              onReset={email=>goLoginWithNotice(email,'Senha redefinida com sucesso. Faça login com sua nova senha.')}/>
          )}
        </div>
      </div>
    </div>
  );
}
